import dayjs from 'dayjs';

import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import type {
  LimaSourceActivationStatusDTO,
  LimaSourceStatusListDTO,
  LimaSourcesResponseModal
} from '@/features/DevicesTable/DevicesTable.types';
import {
  calculateStatusFromStatusCode,
  humanizeTime
} from '@/pages/Administration/Devices/Devices.service';

import SOURCE_TYPE from '../SourceCreate.constants';
import {
  type DataSourceResponse,
  type MicrosoftProviderConfig,
  PROVIDER_TYPE_MAP,
  type SourceCreateDynamicFormModel,
  type SourceDetailResponse,
  SourceDialogMode
} from '../SourceCreate.types';
import { buildSourceName } from '../SourceCreateDialog/SourceCreateDialog.utils';
import type {
  DataProviderConfig,
  DataProvidersModel,
  LogStorageProvider,
  MicrosoftApiName,
  MicrosoftProvider,
  MicrosoftProviderGroup
} from './DataProvider.types';
import { trimObjectValues } from './DataProvider.utils';

const MICROSOFT_PROVIDER_SOURCE_TYPE_NAME_MAP: Record<string, string> = {
  O365: 'O365',
  MicrosoftDefenderUnified: 'Microsoft Defender Unified',
  GraphAPI: 'Microsoft Entra ID',
  O365API: 'Microsoft Entra ID'
};

/**
 * Type guard to check if a provider has an ingest_from field.
 * Used to determine if provider requires date-based ingestion configuration.
 */
export const hasIngestFrom = (
  provider: DataProviderConfig
): provider is LogStorageProvider | MicrosoftProvider => {
  return 'ingest_from' in provider;
};

/**
 * Extracts the maximum status code from a list of items.
 *
 * Business Rule: When a parent source has multiple child data providers,
 * we display the "worst" status (highest status code) to immediately
 * highlight problems. For example, if one provider is failing (code 500)
 * and others are healthy (code 200), we show code 500 on the parent.
 *
 * @param items - Array of sources with optional status arrays
 * @returns The status object with highest code, or undefined if none exist
 */
export const getMaxStatusFromItems = <
  T extends { source_statuses?: LimaSourceStatusListDTO[] }
>(
  items: T[]
): LimaSourceStatusListDTO | undefined => {
  return items
    .map((item) => item.source_statuses?.[0])
    .filter(
      (status): status is LimaSourceStatusListDTO =>
        !!status && typeof status.status_code === 'number'
    )
    .reduce<LimaSourceStatusListDTO | undefined>(
      (max, curr) =>
        !max ||
        (typeof curr.status_code === 'number' &&
          typeof max.status_code === 'number' &&
          curr.status_code > max.status_code)
          ? curr
          : max,
      undefined
    );
};

/**
 * Finds the most recent last_seen timestamp from a list of items.
 *
 * Business Rule: Parent sources show when data was last received from
 * ANY of their child providers. This helps operators quickly identify
 * stale sources without checking each provider individually.
 *
 * @param items - Array of items with optional last_seen timestamps
 * @returns The most recent ISO timestamp, or null if none are valid
 */
export const getMostRecentLastSeen = <T extends { last_seen?: string | null }>(
  items: T[]
): string | null =>
  items.reduce<string | null>((latest, item) => {
    if (!item.last_seen) return latest;

    const current = dayjs.utc(item.last_seen);
    if (!current.isValid()) return latest;

    if (!latest) return current.toISOString();

    const latestDate = dayjs(latest);
    return current.isAfter(latestDate) ? current.toISOString() : latest;
  }, null);

/**
 * Synchronizes parent source status based on child provider status.
 *
 * Business Rule: Parent sources inherit their status from child providers
 * to provide unified monitoring. The status shown on the parent reflects
 * the aggregate health of all its data providers.
 *
 * @param parent - The parent source to update
 * @param maxStatusObj - The maximum (worst) status from child providers
 * @returns Updated parent with synchronized status
 */
export const syncParentStatusFromChild = (
  parent: LimaSourcesResponseModal,
  maxStatusObj?: LimaSourceStatusListDTO | null
): LimaSourcesResponseModal => {
  if (!maxStatusObj) return parent;

  const prev = parent.source_statuses?.[0];

  const fullStatus = prev
    ? {
        ...prev,
        status_code: maxStatusObj.status_code,
        status_code_detail: maxStatusObj.status_code_detail,
        status_updated_at:
          maxStatusObj.status_updated_at ?? dayjs().utc().toISOString(),
        process_details: maxStatusObj.process_details ?? null
      }
    : {
        status_code: maxStatusObj.status_code,
        status_updated_at:
          maxStatusObj.status_updated_at ?? dayjs().utc().toISOString(),
        status_code_detail: maxStatusObj.status_code_detail,
        process_details: maxStatusObj.process_details ?? null
      };

  return {
    ...parent,
    source_statuses: [fullStatus, ...(parent.source_statuses?.slice(1) ?? [])],
    status: calculateStatusFromStatusCode(String(fullStatus.status_code)),
    status_label: maxStatusObj.status_code_detail?.status_label || ''
  };
};

/**
 * Loads S3 data provider values into the form for editing.
 *
 * Business Rule: When editing an S3 source, we pre-populate the form
 * with existing provider configurations. The ingest_from date is converted
 * from UTC (storage format) to local timezone (display format) so users
 * see dates in their timezone.
 *
 * @param defaultValuesModel - Base form defaults
 * @param selectedSourceDetails - Source with S3 providers to load
 * @returns Form values with S3 providers populated
 */
export const loadS3DataProvidersFormValues = (
  defaultValuesModel: SourceCreateDynamicFormModel,
  selectedSourceDetails: SourceDetailResponse
) => {
  const defaultValues = defaultValuesModel;
  const values: { data_provider: DataProvidersModel } = {
    data_provider: {}
  };

  (selectedSourceDetails?.s3_data_providers || []).forEach(
    (provider: DataSourceResponse) => {
      const providerName = provider.name;
      if (providerName) {
        // Convert ingest_from date from UTC to local timezone for display
        const formattedDate = dayjs
          .utc(provider.ingest_from)
          .local()
          .format(YEAR_MONTH_DAY_TIME_FORMAT);

        values.data_provider[providerName as keyof DataProvidersModel] = {
          selected: true,
          prefix: provider.prefix || '',
          log_type: provider?.log_type || [],
          ingest_from: formattedDate || ''
        } as DataProviderConfig;
      }
    }
  );

  // Merge with defaults
  return { ...defaultValues, ...values };
};

/**
 * Builds the data_providers payload for Google Storage sources.
 *
 * Business Rule: Only include selected providers in the payload.
 * The 'selected' field is a UI-only flag and must be excluded from
 * the API payload to match backend expectations.
 *
 * @param values - Form values with data_provider selections
 * @returns Clean payload object ready for API submission
 */
export const buildGoogleStoragePayload = (
  values: SourceCreateDynamicFormModel
) => {
  const entries = Object.entries(values?.data_provider || {});

  // Filter to only selected providers
  const selectedProviders = entries.filter(
    ([, value]) => value.selected === true
  );

  // Build payload: remove 'selected' flag and trim string values
  const payload = selectedProviders.reduce(
    (acc, [key, value]) => {
      const { selected: _selected, ...rest } = value;
      acc[key] = trimObjectValues(rest);
      return acc;
    },
    {} as Record<string, unknown>
  );

  return payload;
};

/**
 * Builds the s3_data_providers array payload for AWS S3 sources.
 *
 * Business Rules:
 * - Only include selected providers
 * - Remove UI-only 'selected' flag
 * - Convert ingest_from from local timezone to UTC for storage
 * - Omit empty log_type arrays to reduce payload noise
 * - Consistent key ordering prevents noisy audit logs
 *
 * @param values - Form values with data_provider selections
 * @returns Array of provider payloads ready for API submission
 */
export const buildS3DataProviderPayload = (
  values: SourceCreateDynamicFormModel
): Array<{ name: string } & Record<string, unknown>> => {
  return Object.entries(values?.data_provider || {})
    .filter(([, value]) => value?.selected)
    .map(([key, value]) => {
      // Type guard: ensure value is LogStorageProvider for destructuring
      const provider = value as LogStorageProvider;
      const {
        selected: _selected,
        log_type,
        ingest_from,
        prefix,
        ...rest
      } = provider;

      // Build payload with consistent key order to prevent audit log noise
      const payload: { name: string } & Record<string, unknown> = {
        name: key,
        ...rest
      };

      // Add prefix if present
      if (prefix !== undefined && prefix !== null) {
        payload.prefix = prefix;
      }

      // Convert ingest_from from local to UTC for storage
      if (ingest_from !== undefined && ingest_from !== null) {
        payload.ingest_from = dayjs(ingest_from)
          .utc()
          .format(YEAR_MONTH_DAY_TIME_FORMAT);
      }

      // Only include log_type if it has values
      if (log_type && Array.isArray(log_type) && log_type.length > 0) {
        payload.log_type = log_type;
      }

      return payload;
    });
};

/**
 * Groups Microsoft sources with their data providers and aggregates status.
 *
 * Business Rule: Microsoft sources use a parent-child model where one
 * "parent" source (e.g., Microsoft 365) has multiple "child" data provider
 * sources (Graph API, O365 API, etc.). This function:
 *
 * 1. Separates parents from children based on parent_source_uid
 * 2. Groups children under their parents
 * 3. Aggregates child statuses to show worst-case on parent
 * 4. Shows most recent last_seen from any child
 *
 * This allows operators to expand/collapse the hierarchy in the UI while
 * seeing aggregate health at a glance.
 *
 * @param sources - Flat array of all Microsoft sources
 * @returns Parent sources with nested data_providers array
 */

export const MICROSOFT_DATA_PROVIDERS_SOURCE_TYPES = [
  SOURCE_TYPE.O365,
  SOURCE_TYPE.MICROSOFT_DEFENDER,
  SOURCE_TYPE.MICROSOFT_ENTRA_ID
];
export const buildMicrosoftGroupedSources = (
  sources: LimaSourcesResponseModal[]
): LimaSourcesResponseModal[] => {
  // Group child sources by their parent_source_uid
  const { parents, grouped, parentIndex } = sources.reduce<{
    parents: LimaSourcesResponseModal[];
    grouped: Map<string, LimaSourcesResponseModal[]>;
    parentIndex: Map<string, number>;
  }>(
    (acc, src) => {
      if (
        src.source_type_id === SOURCE_TYPE.MICROSOFT &&
        src.parent_source_uid === null
      ) {
        acc.parents.push({ ...src });
        acc.parentIndex.set(src.source_uid, acc.parents.length - 1);
      } else if (
        src.parent_source_uid !== null &&
        MICROSOFT_DATA_PROVIDERS_SOURCE_TYPES.includes(src.source_type_id)
      ) {
        const parentUid = src.parent_source_uid ?? '';
        acc.grouped.set(parentUid, [
          ...(acc.grouped.get(parentUid) ?? []),
          src
        ]);
      }
      return acc;
    },
    {
      parents: [] as LimaSourcesResponseModal[],
      grouped: new Map<string, LimaSourcesResponseModal[]>(),
      parentIndex: new Map<string, number>()
    }
  );

  // Attach grouped children to their parents and aggregate status/timestamps
  grouped.forEach((items, sourceUid) => {
    const parentIdx = parentIndex.get(sourceUid);
    if (parentIdx === undefined) return;

    let parent = parents[parentIdx];

    // Ensure all children have status arrays for aggregation
    const sanitizedItems = items.map((item) => ({
      ...item,
      source_statuses: item.source_statuses ?? []
    }));

    // Find worst status from all children
    const maxStatusObj = getMaxStatusFromItems(sanitizedItems);
    parent = syncParentStatusFromChild(parent, maxStatusObj);

    // Find most recent activity from all children
    const mostRecentLastSeen = getMostRecentLastSeen(items);

    parent = {
      ...parent,
      last_seen: mostRecentLastSeen,
      last_seen_display: humanizeTime(mostRecentLastSeen || '', true),
      data_providers: items
    };

    parents[parentIdx] = parent;
  });

  const parentsByUid = new Map(
    parents.map((parent) => [parent.source_uid, parent])
  );

  return sources.flatMap((source) => {
    const isMicrosoftChild =
      source.parent_source_uid !== null &&
      MICROSOFT_DATA_PROVIDERS_SOURCE_TYPES.includes(source.source_type_id);

    if (isMicrosoftChild) {
      return [];
    }

    if (
      source.source_type_id === SOURCE_TYPE.MICROSOFT &&
      source.parent_source_uid === null
    ) {
      return [parentsByUid.get(source.source_uid) ?? { ...source }];
    }

    return [{ ...source }];
  });
};

/**
 * Formats provider site name by removing spaces and adding prefix.
 *
 * Business Rule: Site names are slugified (spaces removed) and prefixed
 * with organization label to create unique source names.
 *
 * Example: prefix="Org1", siteName="Acme Corp" → "Org1-AcmeCorp"
 *
 * @param siteNamePrefix - Organization prefix (e.g., "Org1")
 * @param siteName - Human-readable site name
 * @returns Formatted source name
 */
const formatProviderSiteName = (
  siteNamePrefix: string = '',
  siteName: string = ''
): string => {
  const cleanedName = siteName.replace(/\s+/g, '');
  const suffix = cleanedName ? `-${cleanedName}` : '';
  return `${siteNamePrefix}${suffix}`;
};

const getMicrosoftApiEndpointLabel = (providerKey: string): string => {
  if (providerKey === 'GraphAPI') {
    return 'Graph API';
  }

  if (providerKey === 'O365API') {
    return 'O365 API';
  }

  return '';
};

const buildMicrosoftProviderSourceName = (
  orgLabel: string,
  providerKey: string,
  provider: MicrosoftProvider
): string => {
  if (!orgLabel.trim()) {
    return (
      provider.source_name ??
      formatProviderSiteName(provider.site_name_prefix, provider.site_name)
    );
  }

  return buildSourceName(
    orgLabel,
    provider.source_type?.name ??
      MICROSOFT_PROVIDER_SOURCE_TYPE_NAME_MAP[providerKey] ??
      providerKey,
    PROVIDER_TYPE_MAP[providerKey],
    provider.site_name,
    getMicrosoftApiEndpointLabel(providerKey)
  );
};

/**
 * Transforms form date to UTC ISO string for storage.
 *
 * Business Rule: Dates in the form are in local timezone but must be
 * stored in UTC to ensure consistent behavior across different user
 * timezones and prevent data ingestion from starting at wrong times.
 *
 * @param ingestFrom - Date string in local timezone
 * @returns UTC date string in YEAR_MONTH_DAY_TIME_FORMAT
 */
const formatIngestDate = (ingestFrom: string): string => {
  return dayjs(ingestFrom).utc().format(YEAR_MONTH_DAY_TIME_FORMAT);
};

/**
 * Builds connection inputs for a Microsoft data provider.
 *
 * Connection inputs contain provider-specific configuration that's stored
 * separately from the main source properties in the backend schema.
 *
 * @param providerKey - Provider identifier (e.g., "O365", "GraphAPI")
 * @param provider - Provider form data
 * @returns Connection inputs object for API payload
 */
const buildConnectionInputs = (
  providerKey: string,
  provider: MicrosoftProvider
): Record<string, unknown> => {
  return {
    name: providerKey,
    source_uid: provider.source_uid,
    subscription_type: provider.subscription_type,
    category: provider.dataprovider_category ?? [],
    time_interval: provider.time_interval,
    ingest_from: provider.ingest_from
      ? formatIngestDate(provider.ingest_from)
      : '',
    api_url: provider.api_url ?? ''
  };
};

/**
 * Builds a complete data provider payload for Microsoft sources.
 *
 * @param providerKey - Provider identifier (e.g., "O365", "MicrosoftDefenderUnified")
 * @param provider - Provider form data
 * @param orgId - Organization ID
 * @param mode - Dialog mode ('New' or 'Edit')
 * @returns Complete payload ready for API submission
 */
const buildDataProvider = (
  providerKey: string,
  provider: MicrosoftProvider,
  orgLabel: string,
  orgId: number,
  mode: SourceDialogMode
) => {
  return {
    source_type_id: PROVIDER_TYPE_MAP[providerKey],
    organization_id: orgId,
    source_uid: provider.dataprovider_source_uid ?? '',
    source_name: buildMicrosoftProviderSourceName(
      orgLabel,
      providerKey,
      provider
    ),
    site_name: provider.site_name,
    // Only include 'active' field in Edit mode to avoid backend validation errors
    ...(mode === SourceDialogMode.Edit && {
      active: Boolean(provider.active)
    }),
    connection_inputs: buildConnectionInputs(providerKey, provider)
  };
};

/**
 * Extracts Microsoft Entra ID (formerly Azure AD) provider payloads.
 *
 * Business Rule: Microsoft Entra ID has two sub-providers that can be
 * enabled independently:
 * - Graph API: Modern API for Azure AD logs
 * - O365 API: Legacy API for Office 365 audit logs
 *
 * Each sub-provider requires its own api_endpoint_type field to route
 * requests to the correct Microsoft endpoint.
 *
 * @param entraProvider - Microsoft Entra ID provider group from form
 * @param orgId - Organization ID
 * @param mode - Dialog mode ('New' or 'Edit')
 * @returns Array of provider payloads (0-2 items)
 */
const extractMicrosoftEntraProviders = (
  entraProvider: MicrosoftProviderGroup | undefined,
  orgLabel: string,
  orgId: number,
  mode: SourceDialogMode
) => {
  const providers: MicrosoftProviderConfig[] = [];

  if (entraProvider?.GraphAPI?.selected) {
    const dp = buildDataProvider(
      'GraphAPI',
      entraProvider.GraphAPI,
      orgLabel,
      orgId,
      mode
    );
    providers.push({
      ...dp,
      connection_inputs: {
        ...(dp.connection_inputs || {}),
        api_endpoint_type: 'Graph API'
      }
    });
  }

  if (entraProvider?.O365API?.selected) {
    const dp = buildDataProvider(
      'O365API',
      entraProvider.O365API,
      orgLabel,
      orgId,
      mode
    );
    providers.push({
      ...dp,
      connection_inputs: {
        ...(dp.connection_inputs || {}),
        api_endpoint_type: 'O365 API'
      }
    });
  }

  return providers;
};

/**
 * Builds the complete Microsoft source payload with all selected data providers.
 *
 * Business Rule: Microsoft sources support multiple authentication methods
 * and multiple data providers. The payload structure varies based on:
 *
 * - Authentication mode: Client Secret vs Certificate
 * - Certificate flow: Existing certificate vs Generate new certificate
 * - Selected providers: Standalone O365, Defender, and/or Entra ID sub-providers
 *
 * All providers share the same parent-level authentication credentials
 * (client_id, tenant_id, etc.) but have independent connection_inputs.
 *
 * @param input - Form data with all fields
 * @param mode - Dialog mode ('New' or 'Edit')
 * @param authenticationMode - 'client_secret' or 'certificate'
 * @param newCertificate - True if generating a new certificate
 * @returns Complete payload for Microsoft source creation/update
 */
export const buildMicrosoftPayload = (
  input: SourceCreateDynamicFormModel,
  mode: SourceDialogMode,
  authenticationMode: string,
  newCertificate: boolean,
  orgLabel: string = ''
) => {
  const {
    device_uid,
    source_type_id,
    ip_address,
    org_id,
    organization_name,
    source_name,
    site_name,
    client_id,
    tenant_id,
    contact_email,
    client_secret,
    expires,
    data_provider
  } = input;
  const canonicalOrgLabel = orgLabel || organization_name || '';
  const data_providers: MicrosoftProviderConfig[] = [];

  /** Standalone O365 (legacy) */
  if (data_provider?.O365?.selected) {
    data_providers.push(
      buildDataProvider(
        'O365',
        data_provider.O365,
        canonicalOrgLabel,
        org_id ?? 0,
        mode
      )
    );
  }

  /** Microsoft Defender Unified */
  if (data_provider?.MicrosoftDefenderUnified?.selected) {
    data_providers.push(
      buildDataProvider(
        'MicrosoftDefenderUnified',
        data_provider?.MicrosoftDefenderUnified,
        canonicalOrgLabel,
        org_id ?? 0,
        mode
      )
    );
  }

  /** Microsoft Entra ID (nested Graph API and O365 API) */
  data_providers.push(
    ...extractMicrosoftEntraProviders(
      data_provider?.MicrosoftEntraID,
      canonicalOrgLabel,
      org_id ?? 0,
      mode
    )
  );

  // Set certificate flow flags based on authentication mode
  const is_certificate_flow = authenticationMode === 'certificate';
  const is_new_certificate_flow = is_certificate_flow && newCertificate;

  const connection_inputs = {
    client_id,
    tenant_id,
    client_secret,
    expires,
    is_certificate_flow,
    is_new_certificate_flow,
    ...(contact_email ? { contact_email } : {}),
    data_providers
  };

  return {
    device_uid,
    source_type_id,
    ip_address,
    organization_id: org_id,
    source_name,
    site_name,
    connection_inputs
  };
};

/**
 * Loads Microsoft data provider values into the form for editing.
 *
 * Business Rule: When editing a Microsoft source, we reconstruct the form
 * state from the nested data providers. Microsoft Entra ID providers are
 * grouped together under MicrosoftEntraID (identified by source_type_id 36),
 * while other providers (O365, Defender) are top-level.
 *
 * Dates are converted from UTC (storage) to local timezone (display).
 *
 * @param defaultValuesModel - Base form defaults
 * @param selectedSourceDetails - Source with Microsoft data providers to load
 * @returns Form values with Microsoft providers populated
 */
export const loadMicrosoftDataProvidersFormValues = (
  defaultValuesModel: SourceCreateDynamicFormModel,
  selectedSourceDetails: SourceDetailResponse
) => {
  const values: { data_provider: DataProvidersModel } = {
    data_provider: {}
  };

  (selectedSourceDetails?.ms_data_providers ?? []).forEach(
    (provider: DataSourceResponse) => {
      const providerName = provider?.connection_inputs?.name;
      if (!providerName) return;

      // Convert ingest_from date from UTC to local timezone for display
      const formattedDate = dayjs
        .utc(provider?.connection_inputs?.ingest_from)
        .local()
        .format(YEAR_MONTH_DAY_TIME_FORMAT);

      const providerEntry: MicrosoftProvider = {
        selected: true,
        source_name: provider?.source_name ?? '',
        site_name: provider?.site_name ?? '',
        source_type_id: provider?.source_type_id ?? 0,
        active: provider?.active ?? false,
        ingest_from: formattedDate ?? '',
        api_url: provider?.connection_inputs?.api_url ?? '',
        dataprovider_source_uid: provider?.source_uid ?? '',
        name: providerName,
        subscription_type: provider?.connection_inputs?.subscription_type ?? '',
        dataprovider_category: provider?.connection_inputs?.category ?? [],
        time_interval: provider?.connection_inputs?.time_interval ?? ''
      };

      // Microsoft Entra ID providers (source_type_id === 36) are nested
      if (provider?.source_type_id === SOURCE_TYPE.MICROSOFT_ENTRA_ID) {
        if (!values.data_provider.MicrosoftEntraID) {
          values.data_provider.MicrosoftEntraID = {};
          values.data_provider.MicrosoftEntraID.selected = true;
          values.data_provider.MicrosoftEntraID.GraphAPI = {};
          values.data_provider.MicrosoftEntraID.O365API = {};
        }
        (values.data_provider.MicrosoftEntraID as MicrosoftProviderGroup)[
          providerName as MicrosoftApiName
        ] = providerEntry;
      } else {
        // Other Microsoft providers are top-level
        values.data_provider[providerName as keyof DataProvidersModel] =
          providerEntry;
      }
    }
  );

  return {
    ...defaultValuesModel,
    ...values
  };
};

/**
 * Determines if the Activate/Deactivate menu should be enabled for a source.
 *
 * Business Rules:
 *
 * 1. Microsoft Certificate Flow: When a source uses certificate authentication
 *    with "Generate New Certificate", it cannot be activated until the certificate
 *    thumbprint ID is uploaded. This prevents sources from trying to authenticate
 *    without valid credentials.
 *
 * 2. Dropbox Flow: Dropbox sources cannot be activated until the user completes
 *    OAuth and an access code is stored. This prevents failed connection attempts.
 *
 * @param row - Source activation status details
 * @param source - Full source object
 * @returns True if activation/deactivation menu should be enabled
 */
export const shouldEnableActivateDeactivateMenu = (
  row: LimaSourceActivationStatusDTO,
  source: LimaSourcesResponseModal
): boolean => {
  const isMicrosoftSource = source.source_type_id === SOURCE_TYPE.MICROSOFT;
  const isDropboxSource = source.source_type_id === SOURCE_TYPE.DROPBOX;

  const isNewCertFlow =
    row?.connection_inputs?.is_new_certificate_flow === true;
  const cert = row?.source_cert?.[0];

  // Microsoft: Disable activation when new cert flow is used but no thumbprint exists
  if (
    isMicrosoftSource &&
    isNewCertFlow &&
    cert &&
    cert.prev_thumbprint_id === null &&
    cert.thumbprint_id === null
  ) {
    return false;
  }

  // Dropbox: Disable activation when access code hasn't been obtained
  if (isDropboxSource && !row?.connection_inputs?.has_access_code) {
    return false;
  }

  return true;
};

/**
 * Determines if the activation toggle should be enabled in the source detail view.
 *
 * Business Rule: Similar to shouldEnableActivateDeactivateMenu but works with
 * the SourceDetailResponse schema. Microsoft sources using new certificate flow
 * cannot be activated until a thumbprint ID is present in source_inputs.
 *
 * @param row - Source detail response
 * @returns True if activation toggle should be enabled
 */
export const isActivateToggleEnabled = (row: SourceDetailResponse): boolean => {
  const isNewCertFlow =
    row?.is_certificate_flow === true && row?.is_new_certificate_flow === true;

  const hasEmptyThumbprintId =
    row?.source_inputs?.some(
      (f) => f.name === 'thumbprint_id' && String(f.value ?? '').trim() === ''
    ) ?? false;

  // Enable toggle only when thumbprint is present in new cert flow
  if (isNewCertFlow) {
    return !hasEmptyThumbprintId;
  }

  // Allow activation for all other flows
  return true;
};
